import { getId, Success, uniqueBy } from "@indietabletop/appkit";
import type { SpaceGits } from "@indietabletop/types";
import * as fmt from "../ui/fmt.ts";
import type {
  AnimalHasItems,
  AnyIncrementStatEffect,
  AnySetStatEffect,
  ItemsCostLimitExceeded,
  ModelIssue,
  WeaponsMaxExceeded,
} from "./common.ts";
import { getAddedRefs, getUpdatedWeaponRefs } from "./diffs.ts";
import { EditMode } from "./EditMode.ts";
import { getEffectsFor, modifyDiceType } from "./effects.ts";
import type { Gang, GitPermissions } from "./Gang.ts";
import { rollTimes } from "./rollTimes.ts";
import type { Treatment } from "./Treatment.ts";
import { GitWeapon } from "./Weapon.ts";

export class Git {
  gang: Gang;
  data: SpaceGits.GitData;

  constructor(gang: Gang, data: SpaceGits.GitData) {
    this.gang = gang;
    this.data = data;
  }

  get id() {
    return this.data.id;
  }

  get name() {
    return this.data.name || this.type.name;
  }

  get isNamed() {
    return !!this.data.name;
  }

  get isDead() {
    return !!this.causeOfDeath;
  }

  get causeOfDeath() {
    return this.treatments
      .find((t) =>
        t.valueOrNull()?.effects.some((e) => e.type === "REMOVE_MODEL"),
      )
      ?.valueOrNull();
  }

  get originalType() {
    return this.ruleset.gits.get(this.data.type.id).valueOrThrow();
  }

  get type() {
    const typeId = this.getSetStatEffectsFor("type").reduce(
      (_, effect) => effect.value,
      this.data.type.id,
    );

    return this.ruleset.gits.get(typeId).valueOrThrow();
  }

  get factionRules() {
    return (
      this.gang.faction?.valueOrNull()?.rules.map((r) => new Success(r)) ?? []
    );
  }

  get attitudeRules() {
    return this.attitude >= 6
      ? [this.ruleset.gitSpecialRules.get("bad_attitude")]
      : [];
  }

  get typeRules() {
    return (
      this.type.rules?.map((r) => this.ruleset.gitSpecialRules.get(r.id)) ?? []
    );
  }

  get ruleset() {
    return this.gang.ruleset;
  }

  get scoot() {
    const value = this.getSetStatEffectsFor("scoot").reduce(
      (_, effect) => effect.value,
      this.type.scoot,
    );

    return fmt.diceRolls(value);
  }

  get headbutt() {
    return modifyDiceType(
      this.type.headbutt,
      this.getIncrementEffectsFor("headbutt"),
    );
  }

  get toughness() {
    const value = this.getIncrementEffectsFor("toughness").reduce(
      (acc, effect) => acc + effect.value,
      this.type.toughness,
    );

    return Math.max(1, value);
  }

  get attitude() {
    const increments = [
      ...this.campaign.filter((item) => item.type === "ATTITUDE_MODIFIED"),
      ...this.getIncrementEffectsFor("attitude"),
    ];

    const value = increments.reduce(
      (acc, item) => acc + item.value,
      this.ruleset.constants.defaultAttitude,
    );

    return Math.min(Math.max(1, value), 6);
  }

  get isAnimal() {
    return !!this.type.rules?.some((rule) => rule.id === "animal");
  }

  get isGnawball() {
    return this.type.id === "gnawball";
  }

  get isRunt() {
    return this.type.id === "runt";
  }

  get isDowntrodden() {
    return !!this.type.rules?.some((rule) => rule.id === "downtrodden");
  }

  get itemCostLimit() {
    const { mode } = this.gang;

    if (!mode.isCheatModeActive && mode.isRandomBuildMode) {
      return 0;
    }

    if (
      !mode.isCheatModeActive &&
      (mode.isManualBuildMode || mode.isCampaignMode)
    ) {
      return this.isDowntrodden
        ? this.ruleset.constants.downtroddenItemCostThreshold
        : Infinity;
    }

    return Infinity;
  }

  can(permission: EditMode, subject: GitPermissions) {
    if (this.isDead) {
      return false;
    }

    if (subject == "weapons" && this.isAnimal) {
      return !!(EditMode.REMOVE & permission);
    }

    if (subject === "gear" && this.isAnimal) {
      return !!(EditMode.REMOVE & permission);
    }

    return !!(this.gang.mode.permissions[subject] & permission);
  }

  get baseCost() {
    // Intentionally using original cost, as type can only be changed via
    // treatments that the user doesn't pay for and therefore adjusting the
    // cost because a treatment was selected feels weird.
    return this.originalType.cost;
  }

  get cost() {
    return (
      this.baseCost +
      this.weapons
        .filter((r) => r.isSuccess)
        .map((r) => r.value)
        .reduce((acc, w) => acc + w.cost * w.count, 0) +
      this.gear
        .filter((r) => r.isSuccess)
        .map((g) => g.value)
        .reduce((acc, g) => acc + g.cost, 0)
    );
  }

  get displayCost() {
    switch (this.gang.mode.capUsageStyle) {
      case "BASE_COST": {
        return this.baseCost;
      }

      case "FULL_COST": {
        return this.cost;
      }

      default: {
        return null;
      }
    }
  }

  get weaponRefs() {
    return this.data.weapons;
  }

  get weapons() {
    return this.weaponRefs
      .map((ref, index) => {
        return this.ruleset.weapons
          .get(ref.id)
          .mapSuccess((weapon) => new GitWeapon(this, index, ref, weapon.data));
      })
      .sort((left, right) => {
        if (left.isSuccess && right.isSuccess) {
          return left.value.compare(right.value);
        }

        // Don't sort failures
        return 0;
      });
  }

  get weaponSpecialRules() {
    return uniqueBy(
      this.weapons.filter((w) => w.isSuccess).flatMap((w) => w.value.rules),
      (item) => (item.isSuccess ? item.value.id : item.failure.missingKey),
    );
  }

  get gearRefs() {
    return this.data.gear;
  }

  get gear() {
    return this.gearRefs.map((ref) => this.ruleset.gear.get(ref.id));
  }

  get treatmentRefs() {
    return this.data.treatments;
  }

  get treatments() {
    return this.treatmentRefs.map((ref) =>
      this.ruleset.treatmentLocations
        .get(ref.locationId)
        .flatMap((location) => location.treatments.get(ref.id)),
    );
  }

  get specialRules() {
    return [
      ...this.factionRules.map((lookup, index) => {
        return { type: "FACTION_RULE" as const, lookup, index };
      }),

      ...this.attitudeRules.map((lookup, index) => {
        return { type: "RULE" as const, lookup, index };
      }),

      ...this.typeRules.map((lookup, index) => {
        return { type: "RULE" as const, lookup, index };
      }),

      ...this.gear.map((lookup, index) => {
        return { type: "GEAR" as const, lookup, index };
      }),

      ...this.treatments.map((lookup, index) => {
        return { type: "TREATMENT" as const, lookup, index };
      }),
    ];
  }

  get weaponsAndGear() {
    return [
      ...this.weapons.filter((l) => l.isSuccess).map((l) => l.value),
      ...this.gear.filter((l) => l.isSuccess).map((l) => l.value),
    ];
  }

  get campaign() {
    return this.data.campaign ?? [];
  }

  get loadoutSummary() {
    return fmt.list([
      ...fmt.aggregateWithCounts(this.weapons),
      ...this.gear,
      ...this.treatments,
    ]);
  }

  get weaponsCount() {
    return this.weapons.reduce(
      (acc, weapon) => acc + (weapon.valueOrNull()?.count ?? 0),
      0,
    );
  }

  get gearCount() {
    return this.gear.reduce((acc) => acc + 1, 0);
  }

  get carryCapacity() {
    return this.getIncrementEffectsFor("carryCapacity").reduce(
      (acc, effect) => acc + effect.value,
      this.ruleset.constants.defaultCarryCapacity,
    );
  }

  get hasPremiumContent() {
    return (
      !!this.factionRules.length ||
      !!this.gear.length ||
      !!this.treatments.length
    );
  }

  get issues() {
    const { mode, ruleset } = this.gang;

    const issues = new Map<string, ModelIssue>();

    if (!this.isAnimal && this.weaponsCount > this.carryCapacity) {
      const issue: WeaponsMaxExceeded = {
        type: "WEAPONS_MAX_EXCEEDED",
        max: this.carryCapacity,
        current: this.weaponsCount,
      };

      issues.set(issue.type, issue);
    }

    if (this.isAnimal && this.weaponsAndGear.length > 0) {
      const issue: AnimalHasItems = {
        type: "ANIMAL_HAS_ITEMS",
      };

      issues.set(issue.type, issue);
    }

    if (
      !mode.isCheatModeActive &&
      mode.isManualBuildMode &&
      this.isDowntrodden &&
      this.weaponsAndGear.some(
        (item) => item.cost > ruleset.constants.downtroddenItemCostThreshold,
      )
    ) {
      const issue: ItemsCostLimitExceeded = {
        type: "ITEMS_COST_LIMIT_EXCEEDED",
        max: ruleset.constants.downtroddenItemCostThreshold,
      };

      issues.set(issue.type, issue);
    }

    return Array.from(issues.values());
  }

  get sortValue() {
    const sortTable: Record<string, number> = {
      klankbot: 1,
      bullorc: 2,
      orc: 3,
      goblin: 4,
      runt: 5,
      gnawball: 6,
    };

    return sortTable[this.type.id] ?? 7;
  }

  private get effects() {
    return [
      ...this.gear.flatMap((l) => l.valueOrNull()?.effects ?? []),
      ...this.treatments.flatMap((l) => l.valueOrNull()?.effects ?? []),
    ];
  }

  getWeaponsDiff(
    nextWeaponRefs: SpaceGits.WeaponRef[],
  ): SpaceGits.GitWeaponsChangedDiff | null {
    const updated = getUpdatedWeaponRefs(this.weaponRefs, nextWeaponRefs);

    if (updated.length === 0) {
      return null;
    }

    return {
      type: "GIT_WEAPONS_CHANGED",
      updated: updated,
      createdTs: Date.now(),
    };
  }

  getGearDiff(nextRefs: SpaceGits.Ref[]): SpaceGits.GearChangedDiff | null {
    const added = getAddedRefs(this.gearRefs, nextRefs);

    if (added.length === 0) {
      return null;
    }

    return {
      type: "GEAR_CHANGED",
      createdTs: Date.now(),
      added,
    };
  }

  getTreatmentsDiff(
    nextRefs: SpaceGits.TreatmentRef[],
    isPaid: boolean,
  ): SpaceGits.TreatmentsChangedDiff | null {
    const added = getAddedRefs(this.treatmentRefs, nextRefs);

    if (added.length === 0) {
      return null;
    }

    return {
      type: "TREATMENTS_CHANGED",
      createdTs: Date.now(),
      isPaid,
      added,
    };
  }

  addWeapons(weapons: SpaceGits.WeaponRef[]) {
    const { isCampaignMode, isCheatModeActive } = this.gang.mode;

    if (isCampaignMode && !isCheatModeActive) {
      const diff = this.getWeaponsDiff(weapons);
      return this.clone({
        campaign: diff ? [...this.campaign, diff] : this.campaign,
        weapons,
      });
    }

    return this.clone({ weapons });
  }

  removeWeapon(atIndex: number) {
    return this.clone({
      weapons: this.data.weapons.filter((_, i) => i !== atIndex),
    });
  }

  addGear(gear: SpaceGits.Ref[]) {
    const { isCampaignMode, isCheatModeActive } = this.gang.mode;

    if (isCampaignMode && !isCheatModeActive) {
      const diff = this.getGearDiff(gear);
      return this.clone({
        campaign: diff ? [...this.campaign, diff] : this.campaign,
        gear,
      });
    }

    return this.clone({ gear });
  }

  removeGear(atIndex: number) {
    return this.clone({
      gear: this.data.gear.filter((_, i) => i !== atIndex),
    });
  }

  addTreatments(treatments: SpaceGits.TreatmentRef[], isPaid: boolean) {
    const { isCampaignMode, isCheatModeActive } = this.gang.mode;

    if (isCampaignMode && !isCheatModeActive) {
      const diff = this.getTreatmentsDiff(treatments, isPaid);
      return this.clone({
        campaign: diff ? [...this.campaign, diff] : this.campaign,
        treatments,
      });
    }

    return this.clone({ treatments });
  }

  removeTreatment(atIndex: number) {
    return this.clone({
      treatments: this.data.treatments.filter((_, i) => i !== atIndex),
    });
  }

  revive(causeOfDeath: Treatment) {
    const treatments = this.treatmentRefs.filter(
      (ref) => !causeOfDeath.matchesRef(ref),
    );

    const campaign = this.campaign.filter((item) => {
      return !(
        item.type === "TREATMENTS_CHANGED" &&
        item.added.some((ref) => causeOfDeath.matchesRef(ref))
      );
    });

    return this.clone({ treatments, campaign });
  }

  modifyAttitude(value: number) {
    return this.clone({
      campaign: [
        ...this.campaign,
        { type: "ATTITUDE_MODIFIED", value, createdTs: Date.now() },
      ],
    });
  }

  clone(overrides?: Partial<SpaceGits.GitData>) {
    return this.gang
      .clone({
        gits: this.gang.data.gits.map((data) => {
          if (data.id === this.id) {
            return { ...data, ...overrides };
          }

          return data;
        }),
      })
      .getGit(this.id)
      .valueOrThrow();
  }

  private getIncrementEffectsFor(stat: AnyIncrementStatEffect["stat"]) {
    return getEffectsFor(this.effects, {
      type: "INCREMENT_STAT",
      stat,
      data: this.data,
    });
  }

  private getSetStatEffectsFor<T extends AnySetStatEffect["stat"]>(stat: T) {
    return getEffectsFor(this.effects, {
      type: "SET_STAT",
      stat,
      data: this.data,
    });
  }

  compare(git: Git) {
    if (this.sortValue === git.sortValue) {
      return this.name.localeCompare(git.name);
    }

    return this.sortValue - git.sortValue;
  }

  setRandomName() {
    const nameGenerator = this.isGnawball
      ? this.ruleset.gnawballNameGenerator
      : this.ruleset.orcNameGenerator;

    this.data.name = nameGenerator.generate();

    return this;
  }

  lobotomize() {
    this.data.weapons = [];
    this.data.gear = [];
    this.data.treatments = [
      { id: "gnawbrain_xenotransplant", locationId: "head" },
    ];

    return this;
  }

  setRandomLoot(options: { count: number }) {
    this.data.weapons = [];
    this.data.gear = [];

    const looted = rollTimes(
      options.count,
      () => this.ruleset.getRandomGitLoot(),
      { getKey: (loot) => loot.item.id },
    );

    for (const loot of looted) {
      if (loot.type === "GEAR") {
        this.data.gear.push({ id: loot.item.id });
      }

      if (loot.type === "WEAPON") {
        this.data.weapons.push({ id: loot.item.id, count: 1 });
      }
    }

    return this;
  }

  setRandomTreatment(options: { count: number }) {
    this.data.treatments = [];

    const treatments = rollTimes(
      options.count,
      () => {
        return this.ruleset.getRandomTreatmentLocation().getRandomTreatment();
      },
      {
        getKey: (i) => `${i.location.id}/${i.id}`,
        shouldReroll: (treatment) => {
          return (
            // Skip repairs that would result in the model being removed
            treatment.effects.some((e) => e.type === "REMOVE_MODEL") ||
            // If the treatment is based on fancy gear, we want to reroll it
            // if that gear is already owned by this git.
            (treatment.fromGear &&
              this.data.gear.some((ref) => ref.id === treatment.id))
          );
        },
      },
    );

    for (const treatment of treatments) {
      const ref = { locationId: treatment.location.id, id: treatment.id };
      this.data.treatments.push(ref);
    }

    return this;
  }

  static initData(data: {
    name?: SpaceGits.GitData["name"];
    type: SpaceGits.GitData["type"];
  }): SpaceGits.GitData {
    return {
      id: getId(),
      name: data.name ?? "",
      type: data.type,
      gear: [],
      offenses: [],
      treatments: [],
      weapons: [],
    };
  }
}
